/**
 * Calculates similarity between two normalized tokens in range [0, 1].
 * Uses weighted edit distance with Arabic substitution matrix and clitic handling.
 */

const CLITIC_PREFIXES = [
  'وال', 'فال', 'بال', 'كال', 'ولل',
  'ال', 'و', 'ف', 'ب', 'ل', 'ك'
];

/**
 * Returns substitution cost between two characters in Arabic context.
 * 0.0 for identical
 * 0.2 for ة vs ه
 * 0.25 for hamza forms (ؤ, ئ, ء, ا)
 * 1.0 for standard substitution
 */
function charSubstitutionCost(a: string, b: string): number {
  if (a === b) return 0.0;

  // ة (taa marbuta) vs ه (haa)
  if ((a === 'ة' && b === 'ه') || (a === 'ه' && b === 'ة')) {
    return 0.2;
  }

  // Hamza forms: ؤ (waw with hamza), ئ (yaa with hamza), ء (standalone hamza), ا (alef)
  const isHamzaA = a === 'ؤ' || a === 'ئ' || a === 'ء' || a === 'ا';
  const isHamzaB = b === 'ؤ' || b === 'ئ' || b === 'ء' || b === 'ا';
  if (isHamzaA && isHamzaB) {
    return 0.25;
  }

  return 1.0;
}

/**
 * Weighted Levenshtein distance
 */
export function weightedEditDistance(s1: string, s2: string): number {
  const m = s1.length;
  const n = s2.length;

  if (m === 0) return n;
  if (n === 0) return m;

  // Use two rows for memory efficiency O(min(m, n))
  let prevRow = new Float32Array(n + 1);
  let currRow = new Float32Array(n + 1);

  for (let j = 0; j <= n; j++) {
    prevRow[j] = j;
  }

  for (let i = 1; i <= m; i++) {
    currRow[0] = i;
    const ch1 = s1[i - 1]!;

    for (let j = 1; j <= n; j++) {
      const ch2 = s2[j - 1]!;
      const subCost = charSubstitutionCost(ch1, ch2);

      const costSub = prevRow[j - 1]! + subCost;
      const costDel = prevRow[j]! + 1.0;
      const costIns = currRow[j - 1]! + 1.0;

      currRow[j] = Math.min(costSub, costDel, costIns);
    }

    // Swap rows
    const temp = prevRow;
    prevRow = currRow;
    currRow = temp;
  }

  return prevRow[n]!;
}

/**
 * Strips known Arabic proclitics (و، ف، ب، ك، ل، ال، وال، etc.) if remainder is at least 2 chars
 */
function getCliticVariants(word: string): string[] {
  const variants: string[] = [word];
  for (const prefix of CLITIC_PREFIXES) {
    if (word.startsWith(prefix) && word.length - prefix.length >= 2) {
      variants.push(word.slice(prefix.length));
    }
  }
  return variants;
}

/**
 * Compares two normalized token strings and returns a similarity score in [0, 1].
 */
export function tokenSimilarity(t1: string, t2: string): number {
  if (!t1 || !t2) return 0;
  if (t1 === t2) return 1.0;

  // For very short tokens (<=2 chars), exact match required
  if (t1.length <= 2 || t2.length <= 2) {
    return t1 === t2 ? 1.0 : 0.0;
  }

  // Base raw comparison
  const rawDist = weightedEditDistance(t1, t2);
  const maxLen = Math.max(t1.length, t2.length);
  let maxScore = Math.max(0, 1 - rawDist / maxLen);

  // Compare against clitic-stripped variants (conservative max)
  const v1 = getCliticVariants(t1);
  const v2 = getCliticVariants(t2);

  for (const w1 of v1) {
    for (const w2 of v2) {
      if (w1 === t1 && w2 === t2) continue; // already checked
      if (w1.length <= 2 || w2.length <= 2) {
        if (w1 === w2) {
          // Slight penalty for clitic-only match
          maxScore = Math.max(maxScore, 0.85);
        }
        continue;
      }

      const dist = weightedEditDistance(w1, w2);
      const len = Math.max(w1.length, w2.length);
      const score = Math.max(0, 1 - dist / len) * 0.95; // 5% clitic discount
      if (score > maxScore) {
        maxScore = score;
      }
    }
  }

  return Math.min(1.0, Math.max(0.0, maxScore));
}
